/**
 * Guarda el evento de instalación del navegador.
 *
 * Chrome y Edge disparan `beforeinstallprompt` apenas cargan la página, casi
 * siempre ANTES de que Angular termine de arrancar. Por eso la captura vive
 * acá y se enciende desde `main.ts`: si se escuchara desde el componente del
 * botón, el evento ya habría pasado y el botón nunca se habilitaría.
 *
 * El evento no es estándar (no existe en Safari ni Firefox), así que todo lo
 * que dependa de él tiene que degradar a instrucciones manuales.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

let eventoDiferido: BeforeInstallPromptEvent | null = null;
let yaInstalada = false;
const oyentes = new Set<() => void>();

function avisar(): void {
  oyentes.forEach((fn) => {
    try {
      fn();
    } catch {
      /* un oyente roto no puede tumbar a los demás */
    }
  });
}

/** Se llama una sola vez, desde `main.ts`, antes de arrancar la aplicación. */
export function iniciarCapturaDeInstalacion(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    // Sin esto Chrome muestra su propio aviso y perdemos el control de cuándo
    // ofrecerlo.
    event.preventDefault();
    eventoDiferido = event as BeforeInstallPromptEvent;
    avisar();
  });

  window.addEventListener('appinstalled', () => {
    yaInstalada = true;
    // El evento se consume una sola vez: después de instalar ya no sirve.
    eventoDiferido = null;
    avisar();
  });
}

export function hayEventoDeInstalacion(): boolean {
  return eventoDiferido !== null;
}

/**
 * Dispara el diálogo nativo. Devuelve si el usuario aceptó. El evento se
 * descarta siempre, haya aceptado o no: el navegador no permite reusarlo.
 */
export async function lanzarInstalacion(): Promise<boolean> {
  if (!eventoDiferido) return false;
  const evento = eventoDiferido;
  eventoDiferido = null;
  avisar();
  try {
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}

/** Marcada como instalada por el evento `appinstalled` de esta misma sesión. */
export function seInstaloEnEstaSesion(): boolean {
  return yaInstalada;
}

/** Devuelve la función para desuscribirse. */
export function alCambiarEstadoDeInstalacion(fn: () => void): () => void {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}
